import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useServerSyncedTimer } from "../hooks/useServerSyncedTimer";
import QRCode from "react-qr-code";
import { jsPDF } from "jspdf";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Play, RotateCcw, Sparkles, Users, MapPin, Clock,
  QrCode, Copy, Edit3, Eye, EyeOff, Timer, StopCircle, MessageSquare, UserCircle, ArrowRightLeft,
  Zap, Trophy, RefreshCw, Plus, Download, Check, X, Repeat, ArrowRight, Award, 
  Presentation, Trash2, HelpCircle, ExternalLink, Image, Video, Volume2, VolumeX, CheckCircle, XCircle, Crown, ScanLine, AlertTriangle, Megaphone, Shuffle, History, NotebookPen, Settings, ChevronUp, ChevronDown
} from "lucide-react";
import StationEditor from "../components/StationEditor";
import Scoreboard from "../components/Scoreboard";
import AnswerViewer from "../components/AnswerViewer";
import { TeamAvatar, AvatarSelector } from "../components/TeamAvatar";
import { ThemeProvider, THEMES, ModeToggle, useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if sounds are muted
const isSoundMuted = () => localStorage.getItem("facilitator_muted") === "true";

// Sound for new buzzes (facilitator side)
const playNewBuzzSound = (isFirst = false) => {
  if (isSoundMuted()) return;
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    if (isFirst) {
      // Ding for first buzz
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 880;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
    } else {
      // Quick beep for subsequent buzzes
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 440;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    }
  } catch (e) {
    console.log("Audio not supported");
  }
};

const FacilitatorDashboard = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const lastBuzzCountRef = useRef(0);

  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantCounts, setParticipantCounts] = useState({ counts: {}, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [timerInput, setTimerInput] = useState(300);
  const [isRotating, setIsRotating] = useState(false);
  const [movingParticipant, setMovingParticipant] = useState(null);
  const [buzzes, setBuzzes] = useState([]);

  // Synced timer: derived from session.server_time + timer_end_time
  const timer = useServerSyncedTimer(session);
  const timerRemaining = timer.active ? timer.seconds : null;
  const prevTimerSecondsRef = useRef(null);
  
  // Mute state - persisted to localStorage
  const [isMuted, setIsMuted] = useState(() => {
    return localStorage.getItem("facilitator_muted") === "true";
  });
  
  // Question management state
  const [showQuestionManager, setShowQuestionManager] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showAnnounceDialog, setShowAnnounceDialog] = useState(false);
  const [announceMessage, setAnnounceMessage] = useState("");
  const [announceTarget, setAnnounceTarget] = useState("all");
  const [showReshuffleConfirm, setShowReshuffleConfirm] = useState(false);
  const [rotationReadiness, setRotationReadiness] = useState(null); // { ready, total_pending_members, incomplete: [...] } or null
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [signalsCatalog, setSignalsCatalog] = useState(null); // {sound:[...], movement:[...]}
  const [showMeetTeams, setShowMeetTeams] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [structureDraft, setStructureDraft] = useState({ num_groups: 0, num_stations: 0 });
  const [savingStructure, setSavingStructure] = useState(false);
  const [showShrinkStationsConfirm, setShowShrinkStationsConfirm] = useState(false);
  const [pendingStationsCount, setPendingStationsCount] = useState(null);
  const [showMoveHistory, setShowMoveHistory] = useState(false);
  const [moveHistory, setMoveHistory] = useState([]);
  const [newQuestion, setNewQuestion] = useState({ 
    text: "", 
    answer: "", 
    points: 100, 
    time_limit: 30,
    image_url: "",
    video_url: "",
    audio_url: ""
  });
  
  // Team name editing state
  const [editingTeamName, setEditingTeamName] = useState(null); // { groupNumber, currentName }
  const [teamNameInput, setTeamNameInput] = useState("");
  
  // Team avatar editing state
  const [editingTeamAvatar, setEditingTeamAvatar] = useState(null); // group number

  // Toggle mute function
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    localStorage.setItem("facilitator_muted", newMuted.toString());
    toast.success(newMuted ? "🔇 Sounds muted" : "🔊 Sounds enabled");
  };

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdXuDfm9tc4WKgHBqdYuSg3RseY+XhnNqepqghHJofKKngXRmeKqvf3RieayygXFbcq2yf3BYba2wf3FYaqquf3NZZ6aqf3VcZqGmf3hfZpqif3piZpKcgH5mZ4uXgIFqaYaRgIRtaoKNgIZwa3+KgIhzbHyHgIp1bnqEgIx4cHiCgI56cnaAgI98dHV+gJB+dXR9gJGAd3N8gJKCeHJ7gJOEenF6gJSGenB5gJWIe3B4gJaKfHB3gJeMfXB3gJiOfrV3dwA=");
  }, []);

  // Fetch session data
  const fetchSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`);
      setSession(response.data);
    } catch (error) {
      console.error("Error fetching session:", error);
      // Only show error and redirect on initial load, not during polling
      if (isLoading) {
        toast.error("Failed to load session");
        navigate("/");
      }
      // During polling, silently ignore transient errors
    }
  }, [sessionId, navigate, isLoading]);

  // Fetch participant counts
  const fetchParticipantCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/participants/count`);
      setParticipantCounts(response.data);
    } catch (error) {
      console.error("Error fetching participant counts:", error);
    }
  }, [sessionId]);

  // Fetch all participants
  const fetchParticipants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/participants`);
      setParticipants(response.data.participants);
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  }, [sessionId]);

  // Fetch buzzes with sound notification
  const fetchBuzzes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/buzzes`);
      const newBuzzes = response.data.buzzes;
      
      // Play sound if new buzzes came in
      if (newBuzzes.length > lastBuzzCountRef.current && lastBuzzCountRef.current >= 0) {
        const isFirst = lastBuzzCountRef.current === 0 && newBuzzes.length > 0;
        playNewBuzzSound(isFirst);
      }
      
      lastBuzzCountRef.current = newBuzzes.length;
      setBuzzes(newBuzzes);
    } catch (error) {
      console.error("Error fetching buzzes:", error);
    }
  }, [sessionId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      await fetchSession();
      await fetchParticipantCounts();
      await fetchParticipants();
      await fetchBuzzes();
      setIsLoading(false);
    };
    loadData();
  }, [fetchSession, fetchParticipantCounts, fetchParticipants, fetchBuzzes]);

  // Fetch quiz questions when session is loaded (for quiz or hybrid mode)
  useEffect(() => {
    if (session?.session_mode === 'quiz' || session?.session_mode === 'hybrid') {
      const loadQuestions = async () => {
        try {
          const response = await axios.get(`${API}/sessions/${sessionId}/questions`);
          setQuizQuestions(response.data.questions || []);
        } catch (error) {
          console.error("Error fetching questions:", error);
        }
      };
      loadQuestions();
    }
  }, [session?.session_mode, sessionId]);

  // Polling for updates - 2 second interval for scalability
  useEffect(() => {
    const pollInterval = setInterval(() => {
      fetchSession();
      fetchParticipantCounts();
      fetchParticipants();
      fetchBuzzes();
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [fetchSession, fetchParticipantCounts, fetchParticipants, fetchBuzzes]);

  // Fetch the global signal catalog once (used by "Meet your teams" card)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/signals`);
        if (!cancelled) setSignalsCatalog(res.data);
      } catch {
        /* ignore - card just won't show signal sound text */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Passive rotation-readiness polling for the live "Lobby status" strip
  useEffect(() => {
    if (!sessionId || !session || session.status !== "live") {
      setRotationReadiness(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/sessions/${sessionId}/rotation-readiness`);
        if (!cancelled) setRotationReadiness(res.data);
      } catch {
        /* ignore - strip just stays in its previous state */
      }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [sessionId, session?.status]);

  // ---- Station completion notifications (soft chime when all groups at a station finish) ----
  const completionSinceRef = useRef(
    typeof window !== "undefined"
      ? window.localStorage.getItem(`carousel_completion_since_${sessionId}`) || ""
      : ""
  );
  const playCompletionChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const notes = [
        { freq: 660, start: 0.0, dur: 0.18 },     // E5
        { freq: 880, start: 0.16, dur: 0.22 },    // A5
        { freq: 1318.5, start: 0.34, dur: 0.32 }, // E6
      ];
      notes.forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.connect(gain).connect(ctx.destination);
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.05);
      });
      setTimeout(() => ctx.close(), 900);
    } catch (e) {
      // Browser blocked autoplay or no audio context — silent failure is fine.
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let firstRun = !completionSinceRef.current; // no cursor yet -> first ever load for this session
    const poll = async () => {
      try {
        const since = completionSinceRef.current || "";
        const url = `${API}/sessions/${sessionId}/completion-events${since ? `?since=${encodeURIComponent(since)}` : ""}`;
        const res = await axios.get(url);
        const events = res.data?.events || [];
        if (cancelled || events.length === 0) {
          if (firstRun) {
            // No backlog: anchor cursor to now so subsequent polls only see fresh events.
            const anchor = new Date().toISOString();
            completionSinceRef.current = anchor;
            try { window.localStorage.setItem(`carousel_completion_since_${sessionId}`, anchor); } catch {}
            firstRun = false;
          }
          return;
        }
        const latest = events[events.length - 1].created_at;
        if (firstRun) {
          // Silently absorb backlog on the very first poll — don't chime for events that occurred before the dashboard opened.
          completionSinceRef.current = latest;
          try { window.localStorage.setItem(`carousel_completion_since_${sessionId}`, latest); } catch {}
          firstRun = false;
          return;
        }
        events.forEach((ev) => {
          const teams = (ev.group_numbers || []).map((n) => `Team ${n}`).join(", ");
          toast.success(
            `🎉 ${ev.station_name || `Station ${ev.station_order_index + 1}`} complete — ${teams} finished all answers`,
            { duration: 6000 }
          );
        });
        playCompletionChime();
        completionSinceRef.current = latest;
        try { window.localStorage.setItem(`carousel_completion_since_${sessionId}`, latest); } catch {}
      } catch {
        // ignore polling errors
      }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [sessionId, playCompletionChime]);

  // Timer-end audio cue (fires once when synced timer transitions from positive to 0)
  useEffect(() => {
    const prev = prevTimerSecondsRef.current;
    const curr = timer.active ? timer.seconds : null;
    if (prev !== null && prev > 0 && curr === 0) {
      audioRef.current?.play().catch(() => {});
    }
    prevTimerSecondsRef.current = curr;
  }, [timer.active, timer.seconds]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Actions
  const handleOpenLobby = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/lobby/open`);
      toast.success("Lobby opened! Participants can now join.");
      fetchSession();
    } catch (error) {
      toast.error("Failed to open lobby");
    }
  };

  const handleStartSession = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/start`);
      toast.success("Game started! Let's go!");
      fetchSession();
    } catch (error) {
      toast.error("Failed to start session");
    }
  };

  const handleEndSession = async () => {
    if (!window.confirm("Are you sure you want to end this session?")) return;
    try {
      await axios.post(`${API}/sessions/${sessionId}/end`);
      toast.success("Session ended");
      fetchSession();
    } catch (error) {
      toast.error("Failed to end session");
    }
  };

  const handleResetGame = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/reset`);
      toast.success("Game reset — all participants and results cleared");
      setShowResetConfirm(false);
      fetchSession();
      fetchParticipants();
      fetchParticipantCounts();
    } catch (error) {
      toast.error("Failed to reset game");
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announceMessage.trim()) {
      toast.error("Enter a message first");
      return;
    }
    try {
      await axios.post(`${API}/sessions/${sessionId}/announcement`, {
        message: announceMessage.trim(),
        target_group_id: announceTarget === "all" ? null : announceTarget,
      });
      toast.success("Announcement sent");
      setAnnounceMessage("");
      setShowAnnounceDialog(false);
      fetchSession();
    } catch (error) {
      toast.error("Failed to send announcement");
    }
  };

  const handleClearAnnouncement = async () => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}/announcement`);
      toast.success("Announcement cleared");
      fetchSession();
    } catch (error) {
      toast.error("Failed to clear announcement");
    }
  };

  const handleReshuffle = async () => {
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/teams/reshuffle`);
      toast.success(`Teams reshuffled - ${res.data.moves} member(s) moved`);
      setShowReshuffleConfirm(false);
      fetchParticipants();
    } catch (error) {
      toast.error("Failed to reshuffle teams");
    }
  };

  const openMoveHistory = async () => {
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/moves`);
      setMoveHistory(res.data.moves || []);
      setShowMoveHistory(true);
    } catch (error) {
      toast.error("Failed to load move history");
    }
  };


  const handleAssignLead = async (groupId, participantId) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/teams/${groupId}/assign-lead?participant_id=${participantId}`);
      toast.success("Team lead updated");
      fetchParticipants();
    } catch (error) {
      toast.error("Failed to update team lead");
    }
  };

  const handleClearLead = async (groupId) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/teams/${groupId}/clear-lead`);
      toast.success("Team lead cleared");
      fetchParticipants();
    } catch (error) {
      toast.error("Failed to clear team lead");
    }
  };

  const startEditName = () => {
    setDraftName(session?.name || "");
    setIsEditingName(true);
  };

  // Sync structureDraft from session whenever session settles
  useEffect(() => {
    if (session?.num_groups && session?.num_stations && !savingStructure) {
      setStructureDraft({ num_groups: session.num_groups, num_stations: session.num_stations });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.num_groups, session?.num_stations]);

  const stationsAtRiskOfLoss = (() => {
    if (!session || pendingStationsCount === null) return [];
    return (session.stations || []).slice(pendingStationsCount).filter((s) =>
      (s.questions && s.questions.length > 0) || (s.instructions && s.instructions.trim()) || (s.open_text && s.open_text.trim())
    );
  })();

  const applyStructureChange = async (overrides = {}) => {
    if (!session) return;
    const next = { ...structureDraft, ...overrides };
    if (next.num_groups === session.num_groups && next.num_stations === session.num_stations) return;
    setSavingStructure(true);
    try {
      const res = await axios.patch(`${API}/sessions/${sessionId}/structure`, next);
      setSession(res.data);
      setStructureDraft({ num_groups: res.data.num_groups, num_stations: res.data.num_stations });
      toast.success("Session structure updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update structure");
      // Reset draft to current session values on failure
      setStructureDraft({ num_groups: session.num_groups, num_stations: session.num_stations });
    } finally {
      setSavingStructure(false);
      setShowShrinkStationsConfirm(false);
      setPendingStationsCount(null);
    }
  };

  const requestStructureChange = () => {
    if (!session) return;
    const isShrinkingStations = structureDraft.num_stations < session.num_stations;
    if (isShrinkingStations) {
      // Check if any of the to-be-removed stations have content
      const lost = (session.stations || []).slice(structureDraft.num_stations).filter((s) =>
        (s.questions && s.questions.length > 0) || (s.instructions && s.instructions.trim()) || (s.open_text && s.open_text.trim())
      );
      if (lost.length > 0) {
        setPendingStationsCount(structureDraft.num_stations);
        setShowShrinkStationsConfirm(true);
        return;
      }
    }
    applyStructureChange();
  };

  const cancelEditName = () => {
    setIsEditingName(false);
    setDraftName("");
  };

  const saveSessionName = async () => {
    const next = (draftName || "").trim();
    if (!next) {
      toast.error("Session name cannot be empty");
      return;
    }
    if (next === session?.name) {
      setIsEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const res = await axios.patch(`${API}/sessions/${sessionId}`, { name: next });
      setSession((prev) => (prev ? { ...prev, name: res.data.name } : prev));
      setIsEditingName(false);
      toast.success("Session renamed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename session");
    } finally {
      setSavingName(false);
    }
  };

  const performRotate = async () => {
    setShowRotateConfirm(false);
    setIsRotating(true);
    try {
      await axios.post(`${API}/sessions/${sessionId}/rotate`);
      audioRef.current?.play();
      toast.success("Groups rotated!");
      fetchSession();
    } catch (error) {
      toast.error("Failed to rotate groups");
    } finally {
      setTimeout(() => setIsRotating(false), 1000);
    }
  };

  const handleRotate = async () => {
    setIsRotating(true);
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/rotation-readiness`);
      const readiness = res.data;
      if (!readiness.ready) {
        setRotationReadiness(readiness);
        setShowRotateConfirm(true);
        setIsRotating(false);
        return;
      }
    } catch (error) {
      // Readiness check is best-effort — fall through to rotating anyway
      console.warn("Rotation readiness check failed; proceeding", error);
    }
    setIsRotating(false);
    await performRotate();
  };

  const handleRevealEnergizer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/energizer/reveal`);
      toast.success("Energizer signals revealed!");
      fetchSession();
    } catch (error) {
      toast.error("Failed to reveal energizer");
    }
  };

  const handleHideEnergizer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/energizer/hide`);
      toast.success("Energizer signals hidden");
      fetchSession();
    } catch (error) {
      toast.error("Failed to hide energizer");
    }
  };

  const handleStartTimer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/timer/start?duration=${timerInput}`);
      toast.success("Timer started!");
      fetchSession();
    } catch (error) {
      toast.error("Failed to start timer");
    }
  };

  const handleStopTimer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/timer/stop`);
      toast.success("Timer stopped");
      setTimerRemaining(null);
      fetchSession();
    } catch (error) {
      toast.error("Failed to stop timer");
    }
  };

  // Update team name
  const handleUpdateTeamName = async (groupNumber, newName) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/groups/${groupNumber}/name?name=${encodeURIComponent(newName)}`);
      toast.success(`Team ${groupNumber} renamed to "${newName || `Team ${groupNumber}`}"`);
      setEditingTeamName(null);
      setTeamNameInput("");
      fetchSession();
    } catch (error) {
      toast.error("Failed to update team name");
    }
  };

  // Update team avatar
  const handleUpdateTeamAvatar = async (groupNumber, avatarId) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/groups/${groupNumber}/avatar?avatar_id=${encodeURIComponent(avatarId)}`);
      toast.success("Team avatar updated!");
      setEditingTeamAvatar(null);
      fetchSession();
    } catch (error) {
      toast.error("Failed to update avatar");
    }
  };

  const getTeamDisplayName = (group) => {
    return group.name || `Team ${group.number}`;
  };

  const handleToggleQuestions = async (showQuestions) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}`, { show_questions: showQuestions });
      toast.success(showQuestions ? "Questions visible" : "Questions hidden");
      fetchSession();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Buzzer controls
  const handleStartBuzzer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/buzzer/start`);
      lastBuzzCountRef.current = 0; // Reset buzz count for sound tracking
      toast.success("Buzzer started! Participants can now buzz.");
      fetchSession();
      fetchBuzzes();
    } catch (error) {
      toast.error("Failed to start buzzer");
    }
  };

  const handleStopBuzzer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/buzzer/stop`);
      toast.success("Buzzer stopped");
      fetchSession();
    } catch (error) {
      toast.error("Failed to stop buzzer");
    }
  };

  const handleResetBuzzer = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/buzzer/reset`);
      lastBuzzCountRef.current = 0; // Reset for new round
      toast.success("Buzzer reset for next question!");
      fetchBuzzes();
    } catch (error) {
      toast.error("Failed to reset buzzer");
    }
  };

  // Award point to winning team
  const handleAwardPoint = async (groupNumber, points = 100) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/groups/${groupNumber}/score?points=${points}`);
      const label = points > 0 ? `+${points}` : `${points}`;
      toast.success(`${label} points to Team ${groupNumber}!`);
      fetchSession();
    } catch (error) {
      toast.error("Failed to award points");
    }
  };

  // Next round
  const handleNextRound = async () => {
    try {
      const response = await axios.post(`${API}/sessions/${sessionId}/round/next`);
      lastBuzzCountRef.current = 0;
      toast.success(`Round ${response.data.current_round} started!`);
      fetchSession();
      fetchBuzzes();
    } catch (error) {
      toast.error("Failed to start next round");
    }
  };

  // Final scores reveal
  const handleToggleFinalScores = async (show) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/final-scores/toggle?show=${show}`);
      toast.success(show ? "Final scores revealed!" : "Final scores hidden");
      fetchSession();
    } catch (error) {
      toast.error("Failed to toggle final scores");
    }
  };

  const handleMoveParticipant = async (participantId, targetGroupId) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/participants/${participantId}/move?target_group_id=${targetGroupId}`);
      toast.success("Participant moved!");
      setMovingParticipant(null);
      fetchParticipants();
      fetchParticipantCounts();
    } catch (error) {
      toast.error("Failed to move participant");
    }
  };

  // Toggle scoreboard visibility for participants
  const handleToggleScoreboard = async (show) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/scoreboard/toggle?show=${show}`);
      toast.success(show ? "Scoreboard visible to participants" : "Scoreboard hidden");
      fetchSession();
    } catch (error) {
      toast.error("Failed to toggle scoreboard");
    }
  };

  // Export session data
  const handleExportSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/export`);
      const data = response.data;
      
      // Create CSV content
      let csv = `Session: ${data.session_name}\n`;
      csv += `Mode: ${data.session_mode}\n`;
      csv += `Created: ${new Date(data.created_at).toLocaleString()}\n`;
      csv += `Total Participants: ${data.total_participants}\n\n`;
      csv += `SCOREBOARD\n`;
      csv += `Rank,Team,Score\n`;
      data.scoreboard.forEach((team, i) => {
        csv += `${i + 1},${team.name},${team.score}\n`;
      });
      csv += `\nTEAM DETAILS\n`;
      data.teams.forEach(team => {
        csv += `\n${team.name} (Score: ${team.score})\n`;
        csv += `Participants: ${team.participants.join(', ') || 'None'}\n`;
      });
      
      // Download file
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.session_name.replace(/\s+/g, '_')}_results.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Session exported!");
    } catch (error) {
      toast.error("Failed to export session");
    }
  };

  // Export ALL station details to a printable text PDF (offline / manual mode backup)
  const exportStationDetailsPDF = async () => {
    try {
      const TYPE_LABELS = {
        short_text: "Short Text", long_text: "Long Text", true_false: "True/False",
        ranking: "Ranking/Scale", fill_blank: "Fill in the Blank", instruction: "Instruction (no answer)",
      };
      const typeTag = (q) => q.question_type === "instruction"
        ? " [Info]"
        : ` [${TYPE_LABELS[q.question_type] || "Text"}${q.scope === "team" ? " · Team" : ""}]`;

      // Fetch observation notes (best effort; PDF still generates without them)
      let notesByStation = {};
      try {
        const notesRes = await axios.get(`${API}/sessions/${sessionId}/station-notes`);
        (notesRes.data?.notes || []).forEach((n) => {
          if (!notesByStation[n.station_id]) notesByStation[n.station_id] = [];
          notesByStation[n.station_id].push(n);
        });
      } catch (e) {
        console.warn("Could not load station notes for PDF:", e);
      }

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 48;
      const maxW = pageW - margin * 2;
      let y = margin;

      const ensureSpace = (needed) => {
        if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
      };
      const write = (text, size, style, color, indent = 0, gapAfter = 4) => {
        doc.setFont("helvetica", style);
        doc.setFontSize(size);
        doc.setTextColor(color[0], color[1], color[2]);
        doc.splitTextToSize(String(text), maxW - indent).forEach((line) => {
          ensureSpace(size + 4);
          doc.text(line, margin + indent, y);
          y += size + 4;
        });
        y += gapAfter;
      };

      write(session.name || "Workshop", 20, "bold", [25, 25, 25], 0, 2);
      write(`Facilitator run-sheet · ${session.stations.length} station(s) · Mode: ${session.session_mode}`, 10, "normal", [120, 120, 120], 0, 2);
      write(`Generated ${new Date().toLocaleString()}`, 9, "normal", [150, 150, 150], 0, 6);
      if (session.groups?.length) {
        write(`Teams (${session.groups.length}): ${session.groups.map(g => getTeamDisplayName(g)).join(", ")}`, 10, "normal", [80, 80, 80], 0, 10);
      }

      session.stations.forEach((station, idx) => {
        ensureSpace(50);
        doc.setDrawColor(220, 220, 220);
        doc.line(margin, y, pageW - margin, y); y += 16;
        write(`Station ${idx + 1}: ${station.name || "Untitled"}`, 15, "bold", [180, 30, 110], 0, 4);
        if (station.hide_questions_on_screen) {
          write("(Projector shows instructions only — questions answered on devices)", 8, "italic", [150, 150, 150], 0, 4);
        }
        if (station.instructions?.trim()) {
          write("Instructions", 11, "bold", [40, 40, 40], 0, 2);
          write(station.instructions.trim(), 10, "normal", [40, 40, 40], 12, 8);
        }
        const questions = station.questions || [];
        if (questions.length) {
          write("Questions", 11, "bold", [40, 40, 40], 0, 4);
          questions.forEach((q, qi) => {
            const hasParts = q.parts && q.parts.length > 0;
            if (hasParts) {
              write(`${qi + 1}. ${q.text || "Question set"}`, 10, "bold", [40, 40, 40], 12, 2);
              q.parts.forEach((p) => {
                write(`${p.label || "-"}) ${p.text}${typeTag(p)}`, 10, "normal", [60, 60, 60], 28, 2);
                if (p.question_type === "ranking" && p.ranking_items?.length) write(`Items: ${p.ranking_items.join(", ")}`, 9, "italic", [110, 110, 110], 40, 2);
                if (p.question_type === "fill_blank" && p.blank_template) write(`Template: ${p.blank_template}`, 9, "italic", [110, 110, 110], 40, 2);
              });
              y += 4;
            } else {
              write(`${qi + 1}. ${q.text}${typeTag(q)}`, 10, "normal", [60, 60, 60], 12, 2);
              if (q.question_type === "ranking" && q.ranking_items?.length) write(`Items: ${q.ranking_items.join(", ")}`, 9, "italic", [110, 110, 110], 28, 2);
              if (q.question_type === "fill_blank" && q.blank_template) write(`Template: ${q.blank_template}`, 9, "italic", [110, 110, 110], 28, 2);
            }
          });
        } else if (!station.instructions?.trim()) {
          write("No content added.", 9, "italic", [150, 150, 150], 12, 4);
        }

        // Observation notes for this station
        const stationNotes = notesByStation[station.id] || [];
        if (stationNotes.length) {
          y += 4;
          write("Observation Notes", 11, "bold", [40, 40, 40], 0, 4);
          // Group by group_number
          const byGroup = {};
          stationNotes.forEach((n) => {
            if (!byGroup[n.group_number]) byGroup[n.group_number] = { group: [], participants: [] };
            if (n.participant_id) byGroup[n.group_number].participants.push(n);
            else byGroup[n.group_number].group.push(n);
          });
          Object.keys(byGroup).sort((a, b) => Number(a) - Number(b)).forEach((gn) => {
            const grp = session.groups?.find((g) => g.number === Number(gn));
            const teamLabel = grp ? getTeamDisplayName(grp) : `Team ${gn}`;
            write(teamLabel, 10, "bold", [80, 60, 110], 12, 2);
            byGroup[gn].group.forEach((n) => {
              write(`Group note: ${n.note_text}`, 10, "normal", [60, 60, 60], 28, 2);
              write(`— ${n.author || "Host"} · ${new Date(n.updated_at).toLocaleString()}`, 8, "italic", [140, 140, 140], 28, 4);
            });
            byGroup[gn].participants.forEach((n) => {
              write(`${n.participant_name || "Participant"}: ${n.note_text}`, 10, "normal", [60, 60, 60], 28, 2);
              write(`— ${n.author || "Host"} · ${new Date(n.updated_at).toLocaleString()}`, 8, "italic", [140, 140, 140], 28, 4);
            });
          });
        }

        y += 8;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(160, 160, 160);
        doc.text(`${session.name || "Workshop"} · Page ${i} of ${pageCount}`, margin, pageH - 24);
      }

      const safeName = (session.name || "workshop").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      doc.save(`${safeName}_station_details.pdf`);
      toast.success("Station details PDF exported");
    } catch (error) {
      console.error("Error exporting station details:", error);
      toast.error("Failed to export station details");
    }
  };

  const copyJoinLink = () => {
    const link = `${window.location.origin}/join/${session.join_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Join link copied!");
  };

  // Question Management Functions
  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/questions`);
      setQuizQuestions(response.data.questions || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.text.trim()) {
      toast.error("Question text is required");
      return;
    }
    try {
      // Clean up empty media URLs
      const questionToAdd = {
        ...newQuestion,
        image_url: newQuestion.image_url?.trim() || null,
        video_url: newQuestion.video_url?.trim() || null,
        audio_url: newQuestion.audio_url?.trim() || null,
      };
      const allQuestions = [...quizQuestions, questionToAdd];
      await axios.post(`${API}/sessions/${sessionId}/questions`, { questions: allQuestions });
      setNewQuestion({ text: "", answer: "", points: 100, time_limit: 30, image_url: "", video_url: "", audio_url: "" });
      fetchQuestions();
      toast.success("Question added!");
    } catch (error) {
      toast.error("Failed to add question");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}/questions/${questionId}`);
      fetchQuestions();
      toast.success("Question deleted");
    } catch (error) {
      toast.error("Failed to delete question");
    }
  };

  // Presentation Controls
  const handleSetPresentationMode = async (mode) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/presentation/mode?mode=${mode}`);
      toast.success(`Presentation: ${mode}`);
      fetchSession();
    } catch (error) {
      toast.error("Failed to set presentation mode");
    }
  };

  const handleShowQuestion = async (questionIndex) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/presentation/question?question_index=${questionIndex}`);
      toast.success(`Showing question ${questionIndex + 1}`);
      fetchSession();
    } catch (error) {
      toast.error("Failed to show question");
    }
  };

  const openPresentationView = () => {
    window.open(`${window.location.origin}/present/${sessionId}`, '_blank');
  };

  // Answer Control Functions
  const handleSetAnswerState = async (state) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/answer/state?state=${state}`);
      fetchSession();
    } catch (error) {
      toast.error("Failed to update answer state");
    }
  };

  if (isLoading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#E91E8C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const joinUrl = `${window.location.origin}/join/${session.join_code}`;
  const isDraft = session.status === "draft";
  const isLobby = session.status === "lobby";
  const isLive = session.status === "live";
  const isEnded = session.status === "ended";
  const lobbyOpen = session.lobby_open;
  
  // Mode helpers for hybrid support
  const isHybrid = session.session_mode === "hybrid";
  const showQuizControls = session.session_mode === "quiz" || isHybrid;
  const showCarouselControls = session.session_mode === "carousel" || isHybrid;

  // Team Lead warnings: only relevant when stations have team-scoped questions
  const hasTeamScopedQuestions = (session.stations || []).some((st) =>
    (st.questions || []).some((q) => q.scope === "team" || (q.parts || []).some((p) => p.scope === "team"))
  );
  const teamHasLead = (groupId) => participants.some((p) => p.group_id === groupId && p.is_team_lead);
  const teamHasMembers = (groupId) => participants.some((p) => p.group_id === groupId);
  const teamsNeedingLead = hasTeamScopedQuestions
    ? session.groups.filter((g) => teamHasMembers(g.id) && !teamHasLead(g.id)).length
    : 0;

  return (
    <ThemeProvider theme={session.theme || 'reshapers'}>
    <div className="min-h-screen p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-[#E2D9F3] hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            {isEditingName ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="session-rename-editor">
                <Input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveSessionName();
                    if (e.key === "Escape") cancelEditName();
                  }}
                  maxLength={100}
                  autoFocus
                  disabled={savingName}
                  className="bg-white/5 border-white/20 text-white text-2xl md:text-3xl font-bold h-auto py-2 min-w-[260px]"
                  data-testid="session-rename-input"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={saveSessionName}
                    disabled={savingName}
                    className="btn-primary-gradient control-btn"
                    data-testid="session-rename-save"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> {savingName ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEditName}
                    disabled={savingName}
                    className="text-[#E2D9F3]"
                    data-testid="session-rename-cancel"
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="session-name">
                  {session.name}
                </h1>
                <button
                  type="button"
                  onClick={startEditName}
                  className="p-2 rounded-lg text-[#B49FCC] hover:text-white hover:bg-white/10 transition-colors"
                  title="Rename session"
                  aria-label="Rename session"
                  data-testid="session-rename-btn"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isLive ? "bg-[#00E676] text-black" : 
                isLobby ? "bg-themed-primary text-white" :
                isEnded ? "bg-[#6B5E7C] text-white" : 
                "bg-[#FFC400] text-black"
              }`} data-testid="session-status">
                {isDraft ? "SETUP" : session.status.toUpperCase()}
              </span>
              {!lobbyOpen && isDraft && (
                <span className="text-xs text-[#FF9800] bg-[#FF9800]/20 px-2 py-1 rounded">
                  Participants cannot join yet
                </span>
              )}
              <span className="text-[#E2D9F3] text-sm font-mono" data-testid="join-code">
                Join: {session.join_code}
              </span>
              <span className="text-[#CCFF00] text-sm font-mono" data-testid="facilitator-code" title="Save this code to return to your session">
                Facilitator: {session.facilitator_code}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2">
            {/* Dark/Light Mode Toggle */}
            <ModeToggle />

            {/* Mute Button */}
            <Button
              data-testid="mute-btn"
              onClick={toggleMute}
              variant="outline"
              className={`btn-ghost ${isMuted ? 'text-[#FF1744]' : ''}`}
              title={isMuted ? "Sounds muted" : "Sounds enabled"}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>

            <Dialog open={showQR} onOpenChange={setShowQR}>
              <DialogTrigger asChild>
                <Button data-testid="show-qr-btn" variant="outline" className="btn-ghost">
                  <QrCode className="w-5 h-5 mr-2" />
                  QR Code
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-heavy border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white">Scan to Join</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center p-4">
                  <div className="qr-container mb-4">
                    <QRCode value={joinUrl} size={200} />
                  </div>
                  <p className="text-[#E2D9F3] text-center mb-2">Participant Code:</p>
                  <p className="text-3xl font-mono font-bold gradient-text mb-4">{session.join_code}</p>
                  {!lobbyOpen && isDraft && (
                    <p className="text-sm text-[#FF9800] bg-[#FF9800]/20 px-3 py-2 rounded-lg mb-4">
                      ⚠️ Lobby not open yet. Click "Open Lobby" first!
                    </p>
                  )}
                  <Button onClick={copyJoinLink} className="btn-ghost mb-4">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Join Link
                  </Button>
                  
                  <div className="border-t border-white/10 pt-4 w-full text-center">
                    <p className="text-xs text-[#6B5E7C] mb-2">Save this to return to your session:</p>
                    <p className="text-lg font-mono font-bold text-[#CCFF00]">{session.facilitator_code}</p>
                    <Button 
                      onClick={() => {
                        navigator.clipboard.writeText(session.facilitator_code);
                        toast.success("Facilitator code copied!");
                      }} 
                      variant="ghost" 
                      size="sm"
                      className="mt-2 text-[#CCFF00]"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy Facilitator Code
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button
              data-testid="welcome-screen-btn"
              onClick={() => window.open(`/welcome/${sessionId}`, '_blank')}
              variant="outline"
              className="btn-ghost"
              title="Open a projectable Welcome / Scan-to-Join screen"
            >
              <ScanLine className="w-5 h-5 mr-2" />
              Welcome Screen
            </Button>

            <Button
              data-testid="announce-btn"
              onClick={() => setShowAnnounceDialog(true)}
              variant="outline"
              className="btn-ghost"
              title="Push a message to participants"
            >
              <Megaphone className="w-5 h-5 mr-2" />
              Announce
            </Button>

            <Button
              data-testid="show-participants-btn"
              onClick={() => setShowParticipants(true)}
              variant="outline"
              className="btn-ghost"
            >
              <UserCircle className="w-5 h-5 mr-2" />
              People
            </Button>
            
            {!isEnded && (
              <>
                {isDraft && !lobbyOpen ? (
                  <Button
                    data-testid="open-lobby-btn"
                    onClick={handleOpenLobby}
                    className="btn-primary-gradient control-btn"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    Open Lobby
                  </Button>
                ) : (isDraft || isLobby) ? (
                  <Button
                    data-testid="start-session-btn"
                    onClick={handleStartSession}
                    className="btn-action control-btn"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Start Game
                  </Button>
                ) : isLive ? (
                  <Button
                    data-testid="end-session-btn"
                    onClick={handleEndSession}
                    variant="destructive"
                    className="control-btn"
                  >
                    <StopCircle className="w-5 h-5 mr-2" />
                    End
                  </Button>
                ) : null}
              </>
            )}

            {(lobbyOpen || isLive || isEnded || participants.length > 0) && (
              <Button
                data-testid="reset-game-btn"
                onClick={() => setShowResetConfirm(true)}
                variant="outline"
                className="btn-ghost text-[#FF9800] hover:text-[#FF1744]"
                title="Remove all participants and results, reset to setup"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Reset Game
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Reset Game Confirmation */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="glass-heavy border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[#FF9800]" /> Reset Game?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-[#E2D9F3] text-sm">
              This removes <strong className="text-white">all participants</strong> and their results
              (answers, scores, buzzes, team leads) and returns the session to <strong className="text-white">Setup</strong>.
            </p>
            <p className="text-[#E2D9F3] text-sm mt-2">
              Your stations, questions, and team names/avatars are <strong className="text-white">kept</strong>. This cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="btn-ghost" onClick={() => setShowResetConfirm(false)} data-testid="reset-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" className="control-btn" onClick={handleResetGame} data-testid="reset-confirm-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Yes, Reset Game
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={showAnnounceDialog} onOpenChange={setShowAnnounceDialog}>
        <DialogContent className="glass-heavy border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#E91E8C]" /> Send Announcement
            </DialogTitle>
          </DialogHeader>
          {session.current_announcement && (
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-[#E91E8C]/10 border border-[#E91E8C]/30">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-[#E91E8C] font-semibold">Currently showing · {session.current_announcement.target_name}</p>
                <p className="text-sm text-white truncate">{session.current_announcement.message}</p>
              </div>
              <button onClick={handleClearAnnouncement} className="text-xs text-[#FFB74D] hover:text-[#FF1744] whitespace-nowrap" data-testid="clear-announcement-btn">Clear</button>
            </div>
          )}
          <div className="space-y-3 py-1">
            <textarea
              value={announceMessage}
              onChange={(e) => setAnnounceMessage(e.target.value)}
              className="input-dark w-full min-h-[100px] rounded-lg p-3"
              placeholder="Type your message to participants..."
              data-testid="announce-message-input"
            />
            <div>
              <label className="text-xs text-[#E2D9F3] mb-1 block">Send to</label>
              <Select value={announceTarget} onValueChange={setAnnounceTarget}>
                <SelectTrigger className="input-dark" data-testid="announce-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {session.groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{getTeamDisplayName(g)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="btn-ghost" onClick={() => setShowAnnounceDialog(false)}>Cancel</Button>
            <Button className="btn-primary-gradient control-btn" onClick={handleSendAnnouncement} data-testid="announce-send-btn">
              <Megaphone className="w-4 h-4 mr-2" /> Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reshuffle Confirmation */}
      <Dialog open={showReshuffleConfirm} onOpenChange={setShowReshuffleConfirm}>
        <DialogContent className="glass-heavy border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shuffle className="w-5 h-5 text-[#8B5CF6]" /> Reshuffle All Teams?
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#E2D9F3] text-sm py-2">
            This randomly redistributes <strong className="text-white">everyone</strong> evenly across the same teams. Team leads will be cleared, and every move is saved to the move history. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="btn-ghost" onClick={() => setShowReshuffleConfirm(false)}>Cancel</Button>
            <Button className="btn-primary-gradient control-btn" onClick={handleReshuffle} data-testid="reshuffle-confirm-btn">
              <Shuffle className="w-4 h-4 mr-2" /> Reshuffle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rotate-with-pending-answers warning */}
      <Dialog open={showShrinkStationsConfirm} onOpenChange={setShowShrinkStationsConfirm}>
        <DialogContent className="glass-heavy border-white/10 max-w-lg" data-testid="shrink-stations-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FF1744]" /> Drop {(session?.num_stations || 0) - (pendingStationsCount ?? 0)} station{((session?.num_stations || 0) - (pendingStationsCount ?? 0)) === 1 ? "" : "s"}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#E2D9F3] text-sm py-1">
            The following station{stationsAtRiskOfLoss.length === 1 ? "" : "s"} {stationsAtRiskOfLoss.length === 1 ? "has" : "have"} content that will be permanently deleted along with any saved answers and observation notes:
          </p>
          <ul className="my-2 space-y-1.5" data-testid="shrink-stations-list">
            {stationsAtRiskOfLoss.map((s) => (
              <li key={s.id} className="glass-light rounded-xl p-3 flex items-center justify-between gap-2 text-sm" data-testid={`shrink-station-${s.order_index}`}>
                <span className="text-white truncate">
                  <span className="text-[#FFB74D] mr-1">Station {s.order_index + 1}</span>
                  · {s.name || "(unnamed)"}
                </span>
                <span className="text-xs text-[#B49FCC] whitespace-nowrap">
                  {s.questions?.length || 0} question{(s.questions?.length || 0) === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              className="btn-ghost"
              onClick={() => { setShowShrinkStationsConfirm(false); setPendingStationsCount(null); }}
              data-testid="shrink-stations-cancel"
            >Cancel</Button>
            <Button
              className="bg-[#FF1744] hover:bg-[#FF1744]/80 control-btn"
              onClick={() => applyStructureChange({ num_stations: pendingStationsCount })}
              disabled={savingStructure}
              data-testid="shrink-stations-confirm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {savingStructure ? "Dropping…" : "Drop and continue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRotateConfirm} onOpenChange={setShowRotateConfirm}>
        <DialogContent className="glass-heavy border-white/10 max-w-lg" data-testid="rotate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FFB74D]" /> Some teams aren't done yet
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#E2D9F3] text-sm py-1" data-testid="rotate-confirm-summary">
            {rotationReadiness?.total_pending_members ?? 0} participant{(rotationReadiness?.total_pending_members ?? 0) === 1 ? "" : "s"} across{" "}
            {rotationReadiness?.incomplete?.length ?? 0} team{(rotationReadiness?.incomplete?.length ?? 0) === 1 ? "" : "s"} haven't finished all expected answers at their current station.
          </p>
          <div className="max-h-72 overflow-y-auto space-y-3 py-2" data-testid="rotate-confirm-incomplete-list">
            {(rotationReadiness?.incomplete || []).map((row) => (
              <div key={`${row.station_id}-${row.group_id}`} className="glass-light rounded-xl p-3" data-testid={`rotate-pending-team-${row.group_number}`}>
                <p className="text-xs uppercase tracking-wide text-[#B49FCC]">
                  {row.team_name} <span className="text-[#6B5E7C]">·</span> at {row.station_name}
                </p>
                <ul className="mt-1.5 text-sm text-white space-y-1">
                  {row.members.map((m) => (
                    <li key={m.participant_id} className="flex items-center justify-between gap-2">
                      <span className="truncate">
                        {m.is_team_lead && <span className="mr-1 text-yellow-400">★</span>}
                        {m.name}
                      </span>
                      <span className="text-xs text-[#E2D9F3] whitespace-nowrap">{m.answered} / {m.expected} answered</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="btn-ghost" onClick={() => setShowRotateConfirm(false)} data-testid="rotate-wait-btn">
              Wait
            </Button>
            <Button className="btn-primary-gradient control-btn" onClick={performRotate} data-testid="rotate-proceed-btn">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Rotate anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move History Dialog */}
      <Dialog open={showMoveHistory} onOpenChange={setShowMoveHistory}>
        <DialogContent className="glass-heavy border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#00B0FF]" /> Team Move History
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 py-1" data-testid="move-history-list">
            {moveHistory.length === 0 ? (
              <p className="text-sm text-[#6B5E7C] italic text-center py-6">No moves yet.</p>
            ) : (
              moveHistory.slice().reverse().map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg glass-light text-sm">
                  <span className="text-white font-medium truncate">{m.participant_name}</span>
                  <span className="text-[#E2D9F3] whitespace-nowrap">
                    Team {m.from_group_number ?? "?"} <ArrowRightLeft className="w-3.5 h-3.5 inline mx-1 text-[#6B5E7C]" /> Team {m.to_group_number ?? "?"}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.move_type === 'reshuffle' ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 'bg-[#00B0FF]/20 text-[#00B0FF]'}`}>
                    {m.move_type}
                  </span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Timer Display - Large when active */}
      {timerRemaining !== null && timerRemaining > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-heavy rounded-2xl p-6 mb-6 text-center"
          data-testid="dashboard-timer"
        >
          <p className="text-[#E2D9F3] text-sm mb-2">Time Remaining</p>
          <p className={`text-6xl md:text-8xl font-bold font-mono ${timerRemaining <= 30 ? "text-[#FF1744]" : "gradient-text"}`} data-testid="dashboard-timer-text">
            {timer.mmss}
          </p>
          <Button
            onClick={handleStopTimer}
            variant="ghost"
            className="mt-4 text-[#E2D9F3]"
          >
            <StopCircle className="w-4 h-4 mr-2" />
            Stop Timer
          </Button>
        </motion.div>
      )}

      {/* Stats Row */}
      <div className={`grid ${showQuizControls && !showCarouselControls ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-4 mb-6`}>
        <div className="stat-card" data-testid="total-participants">
          <Users className="w-6 h-6 text-[#E91E8C] mb-2" />
          <p className="text-3xl font-bold text-white">{participantCounts.total}</p>
          <p className="text-sm text-[#E2D9F3]">Participants</p>
        </div>
        <div className="stat-card" data-testid="num-groups">
          <Users className="w-6 h-6 text-[#8B5CF6] mb-2" />
          <p className="text-3xl font-bold text-white">{session.num_groups}</p>
          <p className="text-sm text-[#E2D9F3]">Teams</p>
        </div>
        {showCarouselControls && (
          <>
            <div className="stat-card" data-testid="num-stations">
              <MapPin className="w-6 h-6 text-[#CCFF00] mb-2" />
              <p className="text-3xl font-bold text-white">{session.num_stations}</p>
              <p className="text-sm text-[#E2D9F3]">Stations</p>
            </div>
            <div className="stat-card" data-testid="rotation-count">
              <RotateCcw className="w-6 h-6 text-[#00B0FF] mb-2" />
              <p className="text-3xl font-bold text-white">{session.rotation_index}</p>
              <p className="text-sm text-[#E2D9F3]">Rotations</p>
            </div>
          </>
        )}
      </div>

      {/* Pre-launch Structure Controls (teams + stations) */}
      {(session.status === "draft" || session.status === "lobby") && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-5 mb-6"
          data-testid="structure-card"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #00B0FF 0%, #8B5CF6 100%))' }}>
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Pre-launch setup</h3>
              <p className="text-xs text-[#B49FCC]">Adjust the number of teams and stations before going live. Locked once the game starts.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Teams */}
            <div className="glass-light rounded-xl p-4" data-testid="structure-teams-block">
              <p className="text-xs uppercase tracking-wide text-[#B49FCC] mb-2">Teams</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStructureDraft((d) => ({ ...d, num_groups: Math.max(1, d.num_groups - 1) }))}
                  disabled={savingStructure || structureDraft.num_groups <= 1}
                  className="w-11 h-11 rounded-xl bg-white/10 text-white text-xl flex items-center justify-center hover:bg-white/20 disabled:opacity-30 active:scale-95 touch-manipulation"
                  data-testid="structure-teams-decrement"
                  aria-label="Decrease number of teams"
                >–</button>
                <span className="text-3xl md:text-4xl font-bold text-white tabular-nums w-14 text-center" data-testid="structure-teams-value">
                  {structureDraft.num_groups}
                </span>
                <button
                  type="button"
                  onClick={() => setStructureDraft((d) => ({ ...d, num_groups: Math.min(60, d.num_groups + 1) }))}
                  disabled={savingStructure || structureDraft.num_groups >= 60}
                  className="w-11 h-11 rounded-xl bg-white/10 text-white text-xl flex items-center justify-center hover:bg-white/20 disabled:opacity-30 active:scale-95 touch-manipulation"
                  data-testid="structure-teams-increment"
                  aria-label="Increase number of teams"
                >+</button>
              </div>
              <p className="text-[11px] text-[#B49FCC] mt-2">
                {participants.length > 0 && structureDraft.num_groups < session.num_groups
                  ? `Shrinking will redistribute ${participants.length} participant${participants.length === 1 ? "" : "s"}.`
                  : "1–60 teams"}
              </p>
            </div>
            {/* Stations */}
            <div className="glass-light rounded-xl p-4" data-testid="structure-stations-block">
              <p className="text-xs uppercase tracking-wide text-[#B49FCC] mb-2">Stations</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStructureDraft((d) => ({ ...d, num_stations: Math.max(1, d.num_stations - 1) }))}
                  disabled={savingStructure || structureDraft.num_stations <= 1}
                  className="w-11 h-11 rounded-xl bg-white/10 text-white text-xl flex items-center justify-center hover:bg-white/20 disabled:opacity-30 active:scale-95 touch-manipulation"
                  data-testid="structure-stations-decrement"
                  aria-label="Decrease number of stations"
                >–</button>
                <span className="text-3xl md:text-4xl font-bold text-white tabular-nums w-14 text-center" data-testid="structure-stations-value">
                  {structureDraft.num_stations}
                </span>
                <button
                  type="button"
                  onClick={() => setStructureDraft((d) => ({ ...d, num_stations: Math.min(20, d.num_stations + 1) }))}
                  disabled={savingStructure || structureDraft.num_stations >= 20}
                  className="w-11 h-11 rounded-xl bg-white/10 text-white text-xl flex items-center justify-center hover:bg-white/20 disabled:opacity-30 active:scale-95 touch-manipulation"
                  data-testid="structure-stations-increment"
                  aria-label="Increase number of stations"
                >+</button>
              </div>
              <p className="text-[11px] text-[#B49FCC] mt-2">1–20 stations · empty slots are added with default names</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            {(structureDraft.num_groups !== session.num_groups || structureDraft.num_stations !== session.num_stations) && (
              <Button
                variant="outline"
                onClick={() => setStructureDraft({ num_groups: session.num_groups, num_stations: session.num_stations })}
                disabled={savingStructure}
                className="btn-ghost"
                data-testid="structure-reset-btn"
              >Reset</Button>
            )}
            <Button
              onClick={requestStructureChange}
              disabled={savingStructure || (structureDraft.num_groups === session.num_groups && structureDraft.num_stations === session.num_stations)}
              className="btn-primary-gradient control-btn"
              data-testid="structure-apply-btn"
            >
              {savingStructure ? "Saving…" : "Apply"}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Meet Your Teams - lobby hype intro */}
      {lobbyOpen && !isLive && !isEnded && session.groups?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-5 mb-6"
          data-testid="meet-your-teams-card"
        >
          <button
            type="button"
            onClick={() => setShowMeetTeams((v) => !v)}
            className="w-full flex items-center justify-between text-left mb-3"
            data-testid="meet-your-teams-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Meet your teams</h3>
                <p className="text-xs text-[#B49FCC]">
                  Each team has been assigned a {session.energizer_mode} signal — show this so participants find their teammates.
                </p>
              </div>
            </div>
            {showMeetTeams ? <ChevronUp className="w-5 h-5 text-[#B49FCC]" /> : <ChevronDown className="w-5 h-5 text-[#B49FCC]" />}
          </button>
          {showMeetTeams && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="meet-your-teams-grid">
              {session.groups.map((g) => {
                const pool = (signalsCatalog?.[g.signal_type || "sound"]) || signalsCatalog?.sound || [];
                const signal = pool.find((s) => s.id === g.signal_id) || null;
                const cue = signal?.instruction || signal?.alternative || (g.signal_type === "movement" ? "Pick a fun movement" : "Pick a fun sound");
                return (
                  <motion.div
                    key={g.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-light rounded-xl p-4 flex items-start gap-3"
                    data-testid={`meet-team-card-${g.number}`}
                  >
                    <div className="text-4xl leading-none select-none" aria-hidden="true">
                      {signal?.icon || "🎯"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-[#B49FCC]">Team {g.number}</p>
                      <p className="text-base font-bold text-white truncate" data-testid={`meet-team-name-${g.number}`}>
                        {g.name?.trim() || `Team ${g.number}`}
                      </p>
                      <p className="text-xs text-[#E2D9F3] mt-0.5">
                        {signal?.name || (g.signal_type === "movement" ? "Movement signal" : "Sound signal")}
                      </p>
                      <p className="text-[11px] text-[#B49FCC] mt-1 leading-snug" data-testid={`meet-team-cue-${g.number}`}>
                        {cue}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* Pre-Start Energizer / Icebreaker - available in the lobby before starting the game */}
      {lobbyOpen && !isLive && !isEnded && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-6 mb-6"
          data-testid="lobby-energizer-card"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}>
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Energizer Icebreaker</h3>
                <p className="text-sm text-[#E2D9F3] mt-0.5 max-w-xl">
                  Reveal each team's {session.energizer_mode} signal so people can find their teammates. Run this before you start the game.
                </p>
              </div>
            </div>
            {session.energizer_revealed ? (
              <Button
                data-testid="lobby-hide-energizer-btn"
                onClick={handleHideEnergizer}
                className="btn-ghost control-btn whitespace-nowrap"
              >
                <EyeOff className="w-5 h-5 mr-2" />
                Hide Signals
              </Button>
            ) : (
              <Button
                data-testid="lobby-reveal-energizer-btn"
                onClick={handleRevealEnergizer}
                className="btn-primary-gradient control-btn whitespace-nowrap"
              >
                <Eye className="w-5 h-5 mr-2" />
                Reveal Signals
              </Button>
            )}
          </div>
        </motion.div>
      )}

      {/* Lobby Readiness Strip (live mode passive indicator) */}
      {isLive && rotationReadiness && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl px-4 py-3 mb-4 flex items-center gap-3 border ${
            rotationReadiness.ready
              ? "bg-emerald-500/10 border-emerald-500/30"
              : "bg-amber-500/10 border-amber-500/30"
          }`}
          data-testid="lobby-status-strip"
          data-ready={rotationReadiness.ready ? "true" : "false"}
        >
          <span
            className={`w-2.5 h-2.5 rounded-full ${
              rotationReadiness.ready
                ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.6)]"
                : "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.55)]"
            }`}
            aria-hidden="true"
          />
          <p className="text-sm flex-1 truncate" data-testid="lobby-status-text">
            {rotationReadiness.ready ? (
              <span className="text-emerald-300">
                <strong className="text-white">All teams done</strong> · ready to rotate ✨
              </span>
            ) : (
              <span className="text-amber-200">
                <strong className="text-white">
                  {rotationReadiness.incomplete.length} team{rotationReadiness.incomplete.length === 1 ? "" : "s"} still answering
                </strong>{" "}
                · {rotationReadiness.total_pending_members} participant{rotationReadiness.total_pending_members === 1 ? "" : "s"} pending
              </span>
            )}
          </p>
        </motion.div>
      )}

      {/* Carousel Mode Controls - Rotation, Timer, Energizer */}
      {isLive && showCarouselControls && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          {/* Rotate Control */}
          <div className="glass-heavy rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[#E91E8C]" />
              Rotation
            </h3>
            <Button
              data-testid="rotate-btn"
              onClick={handleRotate}
              disabled={isRotating}
              className={`w-full btn-action control-btn text-lg py-6 ${isRotating ? "rotate-animation" : ""}`}
            >
              <ArrowRightLeft className="w-6 h-6 mr-2" />
              {isRotating ? "Rotating..." : "ROTATE GROUPS"}
            </Button>
          </div>

          {/* Timer Control */}
          <div className="glass-heavy rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Timer className="w-5 h-5 text-[#8B5CF6]" />
              Timer
            </h3>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="timer-input"
                type="number"
                value={timerInput}
                onChange={(e) => setTimerInput(parseInt(e.target.value) || 60)}
                min={30}
                max={3600}
                className="input-dark"
              />
              <span className="flex items-center text-[#E2D9F3]">sec</span>
            </div>
            {session.timer_active ? (
              <Button
                data-testid="stop-timer-btn"
                onClick={handleStopTimer}
                variant="destructive"
                className="w-full control-btn"
              >
                <StopCircle className="w-5 h-5 mr-2" />
                Stop Timer
              </Button>
            ) : (
              <Button
                data-testid="start-timer-btn"
                onClick={handleStartTimer}
                className="w-full btn-primary-gradient control-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Timer
              </Button>
            )}
          </div>

          {/* Energizer Control */}
          <div className="glass-heavy rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#CCFF00]" />
              Energizer
            </h3>
            {session.energizer_revealed ? (
              <Button
                data-testid="hide-energizer-btn"
                onClick={handleHideEnergizer}
                className="w-full btn-ghost control-btn"
              >
                <EyeOff className="w-5 h-5 mr-2" />
                Hide Signals
              </Button>
            ) : (
              <Button
                data-testid="reveal-energizer-btn"
                onClick={handleRevealEnergizer}
                className="w-full btn-primary-gradient control-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                Reveal Signals
              </Button>
            )}
            <p className="text-xs text-[#E2D9F3] mt-3 text-center">
              Mode: {session.energizer_mode}
            </p>
          </div>
        </motion.div>
      )}

      {/* Quiz Mode Controls - Timer & Energizer (only show if NOT showing carousel controls to avoid duplicate timer) */}
      {isLive && showQuizControls && !showCarouselControls && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
        >
          {/* Question Timer */}
          <div className="glass-heavy rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Timer className="w-5 h-5 text-[#8B5CF6]" />
              Question Timer
            </h3>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="timer-input"
                type="number"
                value={timerInput}
                onChange={(e) => setTimerInput(parseInt(e.target.value) || 30)}
                min={5}
                max={300}
                className="input-dark"
              />
              <span className="flex items-center text-[#E2D9F3]">sec</span>
            </div>
            {session.timer_active ? (
              <Button
                data-testid="stop-timer-btn"
                onClick={handleStopTimer}
                variant="destructive"
                className="w-full control-btn"
              >
                <StopCircle className="w-5 h-5 mr-2" />
                Stop Timer
              </Button>
            ) : (
              <Button
                data-testid="start-timer-btn"
                onClick={handleStartTimer}
                className="w-full btn-primary-gradient control-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Timer
              </Button>
            )}
          </div>

          {/* Energizer Control */}
          <div className="glass-heavy rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#CCFF00]" />
              Energizer
            </h3>
            {session.energizer_revealed ? (
              <Button
                data-testid="hide-energizer-btn"
                onClick={handleHideEnergizer}
                className="w-full btn-ghost control-btn"
              >
                <EyeOff className="w-5 h-5 mr-2" />
                Hide Signals
              </Button>
            ) : (
              <Button
                data-testid="reveal-energizer-btn"
                onClick={handleRevealEnergizer}
                className="w-full btn-primary-gradient control-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                Reveal Signals
              </Button>
            )}
            <p className="text-xs text-[#E2D9F3] mt-3 text-center">
              Mode: {session.energizer_mode}
            </p>
          </div>
        </motion.div>
      )}

      {/* Questions Toggle - For Carousel and Hybrid modes */}
      {isLive && showCarouselControls && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-[#00B0FF]" />
              <div>
                <h3 className="text-lg font-semibold text-white">Discussion Questions</h3>
                <p className="text-sm text-[#E2D9F3]">
                  {session.show_questions ? "Visible to participants" : "Hidden from participants"}
                </p>
              </div>
            </div>
            <Switch
              data-testid="toggle-questions-btn"
              checked={session.show_questions !== false}
              onCheckedChange={handleToggleQuestions}
            />
          </div>
        </motion.div>
      )}

      {/* Quiz Buzzer Section - Show for quiz and hybrid modes when live */}
      {isLive && showQuizControls && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-6 mb-6"
        >
          {/* Round Counter - Quiz Mode */}
          {showQuizControls && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#E91E8C] flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{session.current_round || 1}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Round {session.current_round || 1}</h3>
                  <p className="text-sm text-[#E2D9F3]">Current Question</p>
                </div>
              </div>
              <Button
                onClick={handleNextRound}
                className="btn-action"
                size="sm"
              >
                <ArrowRight className="w-4 h-4 mr-1" />
                Next Round
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6 text-[#FF1744]" />
              <div>
                <h3 className="text-lg font-semibold text-white">Quiz Buzzer</h3>
                <p className="text-sm text-[#E2D9F3]">
                  {session.buzzer_active ? "Buzzer is ACTIVE" : "Buzzer is off"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {session.buzzer_active ? (
                <>
                  <Button
                    data-testid="reset-buzzer-btn"
                    onClick={handleResetBuzzer}
                    className="btn-action"
                    size="sm"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Reset Buzzer
                  </Button>
                  <Button
                    data-testid="stop-buzzer-btn"
                    onClick={handleStopBuzzer}
                    variant="destructive"
                    size="sm"
                  >
                    <StopCircle className="w-4 h-4 mr-1" />
                    Stop
                  </Button>
                </>
              ) : (
                <Button
                  data-testid="start-buzzer-btn"
                  onClick={handleStartBuzzer}
                  className="btn-primary-gradient"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Start Buzzer
                </Button>
              )}
            </div>
          </div>
          
          {/* Buzz Results */}
          {session.buzzer_active && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-[#E2D9F3] mb-3">Buzz Order:</h4>
              {buzzes.length > 0 ? (
                <div className="space-y-2">
                  {buzzes.map((buzz, index) => (
                    <motion.div
                      key={buzz.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        index === 0 
                          ? "bg-gradient-to-r from-[#FFD700]/20 to-transparent border border-[#FFD700]/30" 
                          : "bg-white/5"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 
                          ? "bg-[#FFD700] text-black" 
                          : "bg-[#6B5E7C] text-white"
                      }`}>
                        {index === 0 ? <Trophy className="w-4 h-4" /> : index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">Team {buzz.group_number}</p>
                        <p className="text-xs text-[#E2D9F3]">Buzzed by: {buzz.participant_name}</p>
                      </div>
                      {index === 0 && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleAwardPoint(buzz.group_number, -50)}
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-[#FF1744] hover:bg-[#FF1744]/20 text-xs"
                            title="Wrong answer"
                          >
                            <X className="w-3 h-3 mr-1" />
                            -50
                          </Button>
                          <Button
                            onClick={() => handleAwardPoint(buzz.group_number, 100)}
                            size="sm"
                            className="h-8 px-2 bg-[#00E676] hover:bg-[#00E676]/80 text-black text-xs"
                            title="Correct answer"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            +100
                          </Button>
                          <span className="text-[#FFD700] text-sm font-medium ml-1">1ST!</span>
                        </div>
                      )}
                      {index > 0 && buzzes[0] && (
                        <Button
                          onClick={() => handleAwardPoint(buzz.group_number, 50)}
                          size="sm"
                          className="h-8 px-2 bg-[#FFC400] hover:bg-[#FFC400]/80 text-black text-xs"
                          title="Steal bonus"
                        >
                          <Repeat className="w-3 h-3 mr-1" />
                          +50
                        </Button>
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#6B5E7C]">
                  <Zap className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Waiting for teams to buzz...</p>
                </div>
              )}
            </div>
          )}
          
          {/* Answer Controls - Show when a question is active */}
          {session.current_question_index >= 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-[#E2D9F3] mb-3">Answer Controls:</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleSetAnswerState('correct')}
                  className={`flex-1 ${session.answer_state === 'correct' ? 'bg-[#00E676]' : 'bg-[#00E676]/20 hover:bg-[#00E676]/40'} text-white`}
                  size="sm"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Correct!
                </Button>
                <Button
                  onClick={() => handleSetAnswerState('incorrect')}
                  className={`flex-1 ${session.answer_state === 'incorrect' ? 'bg-[#FF1744]' : 'bg-[#FF1744]/20 hover:bg-[#FF1744]/40'} text-white`}
                  size="sm"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Wrong!
                </Button>
                <Button
                  onClick={() => setShowRevealConfirm(true)}
                  className={`flex-1 ${session.answer_state === 'revealed' ? 'bg-[#8B5CF6]' : 'bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/40'} text-white`}
                  size="sm"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Show Answer
                </Button>
                {session.answer_state !== 'hidden' && (
                  <Button
                    onClick={() => handleSetAnswerState('hidden')}
                    variant="ghost"
                    className="btn-ghost"
                    size="sm"
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Hide
                  </Button>
                )}
              </div>
              {/* Current question answer preview */}
              {quizQuestions[session.current_question_index]?.answer && (
                <div className="mt-3 p-3 glass-light rounded-lg">
                  <p className="text-xs text-[#E2D9F3] mb-1">Answer (only you can see this):</p>
                  <p className="text-[#CCFF00] font-semibold">{quizQuestions[session.current_question_index].answer}</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Presentation View & Question Management - Quiz and Hybrid modes */}
      {/* Quiz Controls - Available in all phases for quiz and hybrid modes */}
      {showQuizControls && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Presentation className="w-6 h-6 text-[#8B5CF6]" />
              <div>
                <h3 className="text-lg font-semibold text-white">Quiz Setup & Presentation</h3>
                <p className="text-sm text-[#E2D9F3]">
                  {isDraft ? "Add questions before opening lobby" : "Project quiz on a big screen"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={openPresentationView}
                className="btn-primary-gradient"
                size="sm"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Open Projector View
              </Button>
              <Button
                onClick={() => setShowQuestionManager(true)}
                variant="outline"
                className="btn-ghost"
                size="sm"
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                Manage Questions ({quizQuestions.length})
              </Button>
            </div>
          </div>

          {/* Quick Presentation Controls - Only show when live or in lobby */}
          {(isLive || isLobby) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <Button
                onClick={() => handleSetPresentationMode('lobby')}
                variant={session.presentation_mode === 'lobby' ? 'default' : 'outline'}
                className={session.presentation_mode === 'lobby' ? 'bg-[#8B5CF6]' : 'btn-ghost'}
                size="sm"
              >
                Lobby
              </Button>
              <Button
                onClick={() => handleSetPresentationMode('buzzer')}
                variant={session.presentation_mode === 'buzzer' ? 'default' : 'outline'}
                className={session.presentation_mode === 'buzzer' ? 'bg-[#FF1744]' : 'btn-ghost'}
                size="sm"
              >
                Buzzer
              </Button>
              <Button
                onClick={() => handleSetPresentationMode('carousel')}
                variant={session.presentation_mode === 'carousel' ? 'default' : 'outline'}
                className={session.presentation_mode === 'carousel' ? 'bg-[#00E676] text-black' : 'btn-ghost'}
                size="sm"
              >
                <MapPin className="w-4 h-4 mr-1" />
                Stations
              </Button>
              <Button
                onClick={() => handleSetPresentationMode('scoreboard')}
                variant={session.presentation_mode === 'scoreboard' ? 'default' : 'outline'}
              className={session.presentation_mode === 'scoreboard' ? 'bg-[#FFD700] text-black' : 'btn-ghost'}
              size="sm"
            >
              Scoreboard
            </Button>
            <Button
              onClick={() => handleSetPresentationMode('final')}
              variant={session.presentation_mode === 'final' ? 'default' : 'outline'}
              className={session.presentation_mode === 'final' ? 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black' : 'btn-ghost'}
              size="sm"
            >
              Final Reveal
            </Button>
          </div>
          )}

          {/* Question Selector - Show when there are questions */}
          {quizQuestions.length > 0 && (isLive || isLobby) && (
            <div className="border-t border-white/10 pt-4">
              <p className="text-sm text-[#E2D9F3] mb-3">Show Question on Projector:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {quizQuestions.map((q, index) => (
                  <Button
                    key={q.id}
                    onClick={() => handleShowQuestion(index)}
                    variant={session.current_question_index === index ? 'default' : 'outline'}
                    className={session.current_question_index === index ? 'bg-[#E91E8C]' : 'btn-ghost'}
                    size="sm"
                  >
                    Q{index + 1}
                  </Button>
                ))}
              </div>
            </div>
          )}
          
          {/* Setup phase hint */}
          {isDraft && quizQuestions.length === 0 && (
            <div className="mt-4 p-4 bg-[#FFC400]/10 border border-[#FFC400]/30 rounded-xl">
              <p className="text-[#FFC400] text-sm">
                ⚡ Click "Manage Questions" above to add your quiz questions before opening the lobby!
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* Scoreboard - Show for quiz/hybrid mode or when there are scores */}
      {isLive && (showQuizControls || session.groups?.some(g => g.score > 0)) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          {/* Scoreboard Controls */}
          <div className="flex items-center justify-between mb-4 p-4 rounded-xl bg-white/10 border border-white/20">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-[#CCFF00]" />
                <span className="text-base font-medium text-white">Show to participants</span>
                <Switch
                  checked={session.show_scoreboard}
                  onCheckedChange={handleToggleScoreboard}
                  className="data-[state=checked]:bg-[#CCFF00]"
                />
                {session.show_scoreboard && (
                  <span className="text-xs text-[#CCFF00] font-medium">LIVE</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleToggleFinalScores(!session.show_final_scores)}
                className={session.show_final_scores ? "bg-[#FFD700] text-black hover:bg-[#FFD700]/80" : "btn-primary-gradient"}
                size="sm"
              >
                <Award className="w-4 h-4 mr-1" />
                {session.show_final_scores ? "Hide Final Scores" : "Reveal Final Scores"}
              </Button>
              <Button
                onClick={handleExportSession}
                variant="ghost"
                size="sm"
                className="text-[#E2D9F3] hover:text-white"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
          
          <Scoreboard 
            sessionId={sessionId} 
            groups={session.groups} 
            onScoreUpdate={fetchSession}
          />
        </motion.div>
      )}

      {/* Groups Overview with Participant Names */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-heavy rounded-2xl p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-white">Teams Overview</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {teamsNeedingLead > 0 && (
              <span
                data-testid="teams-need-lead-warning"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#FF9800]/15 text-[#FFB74D] border border-[#FF9800]/30"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {teamsNeedingLead} team{teamsNeedingLead !== 1 ? 's' : ''} need{teamsNeedingLead === 1 ? 's' : ''} a Team Lead
              </span>
            )}
            <Button
              data-testid="reshuffle-teams-btn"
              onClick={() => setShowReshuffleConfirm(true)}
              variant="outline"
              size="sm"
              className="btn-ghost"
              title="Randomly redistribute everyone across teams"
            >
              <Shuffle className="w-4 h-4 mr-1" />
              Reshuffle
            </Button>
            <Button
              data-testid="move-history-btn"
              onClick={openMoveHistory}
              variant="outline"
              size="sm"
              className="btn-ghost"
              title="View team move history"
            >
              <History className="w-4 h-4 mr-1" />
              Move History
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {session.groups.map((group) => {
            const currentStation = session.stations.find(
              (s) => s.order_index === group.current_station_index
            );
            const groupParticipants = participants.filter(p => p.group_id === group.id);
            const isEditingThis = editingTeamName?.groupNumber === group.number;
            const needsLead = hasTeamScopedQuestions && groupParticipants.length > 0 && !groupParticipants.some(p => p.is_team_lead);

            return (
              <div
                key={group.id}
                data-testid={`group-${group.number}`}
                className="glass-light rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  {isEditingThis ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={teamNameInput}
                        onChange={(e) => setTeamNameInput(e.target.value)}
                        placeholder={`Team ${group.number}`}
                        className="input-dark text-sm h-8 flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateTeamName(group.number, teamNameInput);
                          } else if (e.key === 'Escape') {
                            setEditingTeamName(null);
                            setTeamNameInput("");
                          }
                        }}
                      />
                      <button
                        onClick={() => handleUpdateTeamName(group.number, teamNameInput)}
                        className="text-[#00E676] hover:text-white transition-colors"
                        title="Save"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingTeamName(null);
                          setTeamNameInput("");
                        }}
                        className="text-[#FF1744] hover:text-white transition-colors"
                        title="Cancel"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        {/* Team Avatar */}
                        <div className="relative">
                          <TeamAvatar 
                            teamNumber={group.number} 
                            avatarId={group.avatar_id}
                            size="md"
                            onClick={() => setEditingTeamAvatar(editingTeamAvatar === group.number ? null : group.number)}
                          />
                          {editingTeamAvatar === group.number && (
                            <div className="absolute top-full left-0 mt-2 z-50">
                              <AvatarSelector
                                currentAvatarId={group.avatar_id}
                                onSelect={(avatarId) => handleUpdateTeamAvatar(group.number, avatarId)}
                                onClose={() => setEditingTeamAvatar(null)}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <p className="text-xl font-bold gradient-text">
                              {getTeamDisplayName(group)}
                            </p>
                            <button
                              onClick={() => {
                                setEditingTeamName({ groupNumber: group.number, currentName: group.name });
                                setTeamNameInput(group.name || "");
                              }}
                              className="text-[#6B5E7C] hover:text-[#E91E8C] transition-colors"
                              title="Edit team name"
                              data-testid={`edit-team-${group.number}`}
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="text-xs text-[#E2D9F3]">
                            {currentStation?.name || "No station"}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {needsLead && (
                  <div
                    data-testid={`team-no-lead-${group.number}`}
                    className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 rounded-lg bg-[#FF9800]/15 border border-[#FF9800]/30"
                    title="This team has no Team Lead - tap a member's crown to assign one"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-[#FFB74D] flex-shrink-0" />
                    <span className="text-xs font-medium text-[#FFB74D]">No Team Lead assigned</span>
                  </div>
                )}

                <div className="space-y-2 mt-3">
                  {groupParticipants.length > 0 ? (
                    groupParticipants.map(p => (
                      <div key={p.id} className="flex items-center justify-between text-sm">
                        <span className="text-white flex items-center gap-2">
                          {p.is_team_lead ? (
                            <Crown className="w-4 h-4 text-[#FFC400]" />
                          ) : (
                            <UserCircle className="w-4 h-4 text-[#E91E8C]" />
                          )}
                          {p.name || "Anonymous"}
                          {p.is_team_lead && (
                            <span className="text-[10px] font-semibold text-[#FFC400] uppercase tracking-wide">Lead</span>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => p.is_team_lead ? handleClearLead(group.id) : handleAssignLead(group.id, p.id)}
                            className={`transition-colors ${p.is_team_lead ? 'text-[#FFC400] hover:text-[#FF1744]' : 'text-[#6B5E7C] hover:text-[#FFC400]'}`}
                            title={p.is_team_lead ? "Remove team lead" : "Make team lead"}
                            data-testid={`lead-toggle-${p.id}`}
                          >
                            <Crown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setMovingParticipant(p)}
                            className="text-[#6B5E7C] hover:text-white transition-colors"
                            title="Move to another group"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#6B5E7C] italic">No participants yet</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Stations Editor */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-heavy rounded-2xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#E91E8C]" />
            Stations
          </h3>
          <div className="flex items-center gap-2">
            {session.content_locked && (
              <span className="text-xs text-[#FFC400] bg-[#FFC400]/20 px-2 py-1 rounded">
                Content Locked
              </span>
            )}
            <Button
              onClick={() => setShowAnswers(true)}
              variant="outline"
              size="sm"
              className="btn-ghost"
              data-testid="view-answers-btn"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              View Answers
            </Button>
            <Button
              onClick={exportStationDetailsPDF}
              variant="outline"
              size="sm"
              className="btn-ghost"
              data-testid="export-station-details-btn"
              title="Download a printable PDF of all station details (offline / manual backup)"
            >
              <Download className="w-4 h-4 mr-1" />
              Export Details (PDF)
            </Button>
            <Button
              onClick={() => window.open(`/station/${sessionId}/0`, '_blank')}
              variant="outline"
              size="sm"
              className="btn-ghost"
              title="View station screens for projecting"
            >
              <Presentation className="w-4 h-4 mr-1" />
              Station Screens
            </Button>
          </div>
        </div>
        
        <div className="space-y-3">
          {session.stations.map((station, index) => (
            <div
              key={station.id}
              data-testid={`station-${station.order_index}`}
              className="glass-light rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex-1">
                <p className="font-semibold text-white">{station.name}</p>
                <p className="text-sm text-[#E2D9F3]">
                  {station.questions.length} questions
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => window.open(`/host/${station.host_key}`, '_blank')}
                  variant="ghost"
                  size="sm"
                  className="text-[#FFB74D] hover:text-white"
                  title="Open station host screen (observation notes)"
                  data-testid={`open-host-${station.order_index}`}
                >
                  <NotebookPen className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => {
                    const url = `${window.location.origin}/host/${station.host_key}`;
                    navigator.clipboard.writeText(url);
                    toast.success(`Host link for ${station.name} copied!`);
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-[#CCFF00] hover:text-white"
                  title="Copy host link (share with on-site host)"
                  data-testid={`copy-host-link-${station.order_index}`}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => window.open(`/station/${sessionId}/${index}`, '_blank')}
                  variant="ghost"
                  size="sm"
                  className="text-[#8B5CF6] hover:text-white"
                  title="View station screen"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
                {!session.content_locked && (
                  <Button
                    data-testid={`edit-station-${station.order_index}`}
                    onClick={() => setEditingStation(station)}
                    variant="ghost"
                    size="sm"
                    className="text-[#E2D9F3] hover:text-white"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Station URLs Reference */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-xs text-[#6B5E7C] mb-2">Station Screen URLs (for projecting):</p>
          <div className="space-y-1 text-xs font-mono text-[#E2D9F3]">
            {session.stations.map((station, index) => (
              <div key={station.id} className="flex items-center gap-2">
                <span className="text-[#8B5CF6]">Station {index + 1}:</span>
                <span 
                  className="cursor-pointer hover:text-white"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/station/${sessionId}/${index}`);
                    toast.success(`Station ${index + 1} URL copied!`);
                  }}
                >
                  {window.location.origin}/station/{sessionId}/{index}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6B5E7C] mt-4 mb-2 flex items-center gap-1.5">
            <NotebookPen className="w-3 h-3" /> Station Host links (observation notes · no login):
          </p>
          <div className="space-y-1 text-xs font-mono text-[#E2D9F3]" data-testid="host-links-list">
            {session.stations.map((station, index) => (
              station.host_key ? (
                <div key={`host-${station.id}`} className="flex items-center gap-2">
                  <span className="text-[#FFB74D]">Host {index + 1}:</span>
                  <span
                    className="cursor-pointer hover:text-white truncate"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/host/${station.host_key}`);
                      toast.success(`Host link for Station ${index + 1} copied!`);
                    }}
                    data-testid={`host-link-text-${station.order_index}`}
                  >
                    {window.location.origin}/host/{station.host_key}
                  </span>
                </div>
              ) : null
            ))}
          </div>
        </div>
      </motion.div>

      {/* Station Editor Dialog */}
      <Dialog open={!!editingStation} onOpenChange={() => setEditingStation(null)}>
        <DialogContent className="glass-heavy border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Station</DialogTitle>
          </DialogHeader>
          {editingStation && (
            <StationEditor
              station={editingStation}
              sessionId={sessionId}
              onSave={() => {
                setEditingStation(null);
                fetchSession();
              }}
              onCancel={() => setEditingStation(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Answers Viewer Dialog */}
      <Dialog open={showAnswers} onOpenChange={setShowAnswers}>
        <DialogContent className="glass-heavy border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Station Answers</DialogTitle>
          </DialogHeader>
          {showAnswers && session?.stations && (
            <AnswerViewer sessionId={sessionId} stations={session.stations} />
          )}
        </DialogContent>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog open={showParticipants} onOpenChange={setShowParticipants}>
        <DialogContent className="glass-heavy border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">All Participants ({participants.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {participants.length > 0 ? (
              participants.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 glass-light rounded-lg">
                  <div className="flex items-center gap-3">
                    <UserCircle className="w-5 h-5 text-[#E91E8C]" />
                    <div>
                      <p className="text-white font-medium">{p.name || "Anonymous"}</p>
                      <p className="text-xs text-[#6B5E7C]">Group {p.group_number}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMovingParticipant(p)}
                    className="text-[#E2D9F3] hover:text-white transition-colors flex items-center gap-1 text-sm"
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                    Move
                  </button>
                </div>
              ))
            ) : (
              <p className="text-center text-[#6B5E7C] py-8">No participants have joined yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Participant Dialog */}
      <Dialog open={!!movingParticipant} onOpenChange={() => setMovingParticipant(null)}>
        <DialogContent className="glass-heavy border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">
              Move {movingParticipant?.name || "Participant"}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-[#E2D9F3] mb-4">Select new group:</p>
            <div className="grid grid-cols-2 gap-2">
              {session.groups.map(group => (
                <Button
                  key={group.id}
                  onClick={() => handleMoveParticipant(movingParticipant?.id, group.id)}
                  disabled={group.id === movingParticipant?.group_id}
                  className={group.id === movingParticipant?.group_id ? "btn-ghost opacity-50" : "btn-primary-gradient"}
                >
                  Group {group.number}
                  {group.id === movingParticipant?.group_id && " (current)"}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Manager Dialog */}
      <Dialog open={showQuestionManager} onOpenChange={setShowQuestionManager}>
        <DialogContent className="glass-heavy border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-[#E91E8C]" />
              Manage Quiz Questions
            </DialogTitle>
          </DialogHeader>
          
          {/* Add New Question */}
          <div className="mt-4 p-4 glass-light rounded-xl">
            <h4 className="text-white font-semibold mb-3">Add New Question</h4>
            <div className="space-y-3">
              <Input
                placeholder="Question text..."
                value={newQuestion.text}
                onChange={(e) => setNewQuestion({...newQuestion, text: e.target.value})}
                className="input-dark"
              />
              <Input
                placeholder="Answer (for your reference)"
                value={newQuestion.answer}
                onChange={(e) => setNewQuestion({...newQuestion, answer: e.target.value})}
                className="input-dark"
              />
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-[#E2D9F3] mb-1 block">Points</label>
                  <Input
                    type="number"
                    value={newQuestion.points}
                    onChange={(e) => setNewQuestion({...newQuestion, points: parseInt(e.target.value) || 100})}
                    className="input-dark"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-[#E2D9F3] mb-1 block">Time Limit (sec)</label>
                  <Input
                    type="number"
                    value={newQuestion.time_limit}
                    onChange={(e) => setNewQuestion({...newQuestion, time_limit: parseInt(e.target.value) || 30})}
                    className="input-dark"
                  />
                </div>
              </div>
              
              {/* Media URL Fields */}
              <div className="border-t border-white/10 pt-3 mt-1">
                <p className="text-xs text-[#E2D9F3] mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Add Media (optional)
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-[#8B5CF6] flex-shrink-0" />
                    <Input
                      placeholder="Image URL (jpg, png, gif...)"
                      value={newQuestion.image_url}
                      onChange={(e) => setNewQuestion({...newQuestion, image_url: e.target.value})}
                      className="input-dark text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-[#E91E8C] flex-shrink-0" />
                    <Input
                      placeholder="Video URL (YouTube, Vimeo...)"
                      value={newQuestion.video_url}
                      onChange={(e) => setNewQuestion({...newQuestion, video_url: e.target.value})}
                      className="input-dark text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-[#00E676] flex-shrink-0" />
                    <Input
                      placeholder="Audio URL (mp3, wav...)"
                      value={newQuestion.audio_url}
                      onChange={(e) => setNewQuestion({...newQuestion, audio_url: e.target.value})}
                      className="input-dark text-sm"
                    />
                  </div>
                </div>
              </div>
              
              <Button onClick={handleAddQuestion} className="btn-primary-gradient w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>
          </div>

          {/* Questions List */}
          <div className="mt-4">
            <h4 className="text-white font-semibold mb-3">Questions ({quizQuestions.length})</h4>
            {quizQuestions.length > 0 ? (
              <div className="space-y-2">
                {quizQuestions.map((q, index) => (
                  <div key={q.id} className="flex items-start gap-3 p-3 glass-light rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#8B5CF6] flex items-center justify-center text-white font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">{q.text}</p>
                      {q.answer && (
                        <p className="text-sm text-[#00E676] mt-1">Answer: {q.answer}</p>
                      )}
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#6B5E7C]">
                        <span>{q.points} pts</span>
                        <span>{q.time_limit}s</span>
                        {q.image_url && <span className="flex items-center gap-1"><Image className="w-3 h-3" /> Image</span>}
                        {q.video_url && <span className="flex items-center gap-1"><Video className="w-3 h-3" /> Video</span>}
                        {q.audio_url && <span className="flex items-center gap-1"><Volume2 className="w-3 h-3" /> Audio</span>}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDeleteQuestion(q.id)}
                      variant="ghost"
                      size="sm"
                      className="text-[#FF1744] hover:bg-[#FF1744]/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B5E7C]">
                <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No questions yet. Add some above!</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reveal Answer Confirmation Dialog */}
      <Dialog open={showRevealConfirm} onOpenChange={setShowRevealConfirm}>
        <DialogContent className="glass-heavy border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#8B5CF6]" />
              Reveal Answer?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#E2D9F3] mb-4">
              Are you sure you want to reveal the answer to everyone?
            </p>
            {quizQuestions[session?.current_question_index]?.answer && (
              <div className="p-4 glass-light rounded-xl text-center">
                <p className="text-sm text-[#E2D9F3] mb-2">The answer is:</p>
                <p className="text-2xl font-bold text-[#CCFF00]">
                  {quizQuestions[session?.current_question_index]?.answer}
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowRevealConfirm(false)}
              variant="ghost"
              className="flex-1 btn-ghost"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleSetAnswerState('revealed');
                setShowRevealConfirm(false);
              }}
              className="flex-1 bg-themed-primary hover:opacity-80"
            >
              <Eye className="w-4 h-4 mr-2" />
              Yes, Reveal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </ThemeProvider>
  );
};

export default FacilitatorDashboard;
